import {
  observable,
  action,
  makeObservable,
  runInAction,
} from 'mobx';
import type { ReactNode } from 'react';
import type PublicStore from './PublicStore';
import TranslateRepository from '../repositories/TranslateRepository';

// 서버(i18nLangNames)와 같은 목록을 유지해야 한다.
// 전세계 사용자 수 상위 30개 언어. 서버(ErpService i18nLangNames,
// ErpCache i18nLangs)와 같은 집합을 유지해야 한다. RTL 언어(ar/ur/fa 등)는
// ERP가 RTL 레이아웃을 지원하지 않아 지금은 제외한다.
export const SUPPORTED_LANGUAGES: ReadonlyArray<string> = [
  'en', 'zh', 'hi', 'es', 'fr', 'bn', 'pt', 'ru', 'id', 'de',
  'ja', 'mr', 'te', 'tr', 'ta', 'vi', 'ha', 'sw', 'it', 'jv',
  'pa', 'gu', 'th', 'kn', 'pl', 'uk', 'ms', 'nl', 'ml', 'tl',
];

// 렌더 중 쌓인 미번역 문구를 모아 보내는 간격
const FLUSH_DELAY_MS = 200;

// 서버 상한과 같은 값
const MAX_BATCH = 200;

// 전송 실패(타임아웃/네트워크 등) 연속 발생시 재시도 간격의 시작값/상한값.
// 전송에 실패한 문구는 flush() 가 직접 pending 으로 되돌리고, 그 밖의 문구도
// 해당 컴포넌트가 다시 그려질 때마다 T() 가 dict/cooldown/inFlight 어디에도
// 없으면 다시 pending 에 넣는다. 즉 대기열은 서버 상태와 무관하게 계속
// 채워지므로, 서버가 죽어 있을 때 전송을 막지 않으면 200ms 디바운스 간격으로
// 요청이 끝없이 나간다. 그래서 "적재"가 아니라 "전송" 쪽에 지수 백오프를 건다.
const INITIAL_RETRY_DELAY_MS = 2000;
const MAX_RETRY_DELAY_MS = 60000;

// 서버가 응답에 포함하지 않은 문구를 다시 물어보기까지 기다리는 시간.
const ABSENT_RETRY_DELAY_MS = 60000;

// 지원하지 않는 언어는 항상 'ko' 로 정규화한다. PublicStore.language 도 이 규칙을 따라야
// t.lang 과 어긋나지 않는다.
//
// 두 Go 서비스는 언어를 strings.ToLower 로 비교하는데 여기만 대소문자/지역
// 코드에 민감하면 이 한 지점에서 기능 전체가 조용히 꺼진다. UserModel.lang 은
// 서버가 앞으로 내려줄 예정인 필드라 형식이 아직 확정되지 않았으므로
// 'EN' / 'en-US' / 'en_US' 가 들어와도 모두 'en' 으로 맞춰준다.
// 'ko' 는 지원 언어 목록에 없지만 "원문 그대로 보기" 라는 명시적인 선택으로
// localStorage 에 저장되었다가 다시 들어오므로, 폴백 값과 같아 그대로 통과한다.
export function normalizeLanguage(lang?: string | null): string {
  if (!lang) {
    return 'ko';
  }

  const base = lang.toLowerCase().split(/[-_]/)[0];
  return SUPPORTED_LANGUAGES.indexOf(base) > -1 ? base : 'ko';
}

// axios 오류 객체를 그대로 console 에 넘기면 e.config.data 에 요청 본문이
// 통째로 들어 있어 인증 토큰까지 콘솔/원격 로그에 남는다. 원인 파악에 필요한
// 정보만 문자열로 뽑아 쓴다.
function describeError(e: any): string {
  const status = e?.response?.status;
  const code = e?.code;
  const message = e?.message;
  return `status=${status || '-'} code=${code || '-'} message=${message || '-'}`;
}

export class TranslateStore {
  // 한국어 원문 -> 번역문. observable 이므로 observer 컴포넌트가 추적한다.
  @observable dict: Map<string, string> = new Map<string, string>();

  @observable lang: string = 'ko';

  private publicStore?: PublicStore = undefined;

  // 아래 넷은 렌더 중에 건드리므로 절대 observable 로 만들지 않는다.
  private pending: Set<string> = new Set<string>();

  // 서버 응답에 빠져 있던 문구 -> 다시 물어봐도 되는 시각(ms epoch).
  // 영구 blacklist 가 아니라 유한한 쿨다운이다. 서버는 "영원히 번역하지
  // 않는" 문구(한글 없음/길이 초과/번역 결과가 원문과 동일)를 items[text]
  // = text 로 되돌려주므로, 응답에 키가 없다는 것은 캐시 왕복 실패, 대기
  // 예산 초과, 모델 호출 오류, 청크 패닉 같은 일시적인 사유뿐이다. 그런
  // 경우 서버는 번역을 이미 만들어 저장해 두었을 수도 있는데, 영구히
  // 제외해 버리면 새로고침 전까지 그 결과를 볼 방법이 없다. 그렇다고 바로
  // 다시 물으면 렌더마다 같은 요청이 나가므로 잠깐 쉬었다가 재시도한다.
  private cooldown: Map<string, number> = new Map<string, number>();

  private loaded: Set<string> = new Set<string>();

  // 요청을 보냈지만 아직 응답/오류가 오지 않은 문구. 응답이 오면(성공/실패 모두) 반드시
  // 지워야 한다. 그래야 실패한 문구가 다음 렌더에서 다시 요청 대상이 된다.
  private inFlight: Set<string> = new Set<string>();

  private timer?: ReturnType<typeof setTimeout> = undefined;

  // /i18n/translate 요청이 나가 있는 동안 true. 한 번에 하나만 보낸다.
  // 이 오리진은 WebSocket 업그레이드 때문에 HTTP/2 를 끄고 있어서 번역
  // 요청도 브라우저의 호스트당 연결(약 6개)을 화면 데이터 조회와 나눠
  // 쓴다. 번역 한 번이 수 초씩 걸리므로 동시에 여러 개를 열면 실제 화면
  // 로딩이 그 뒤에 줄을 선다.
  private flushing = false;

  // 연속 전송 실패 횟수에 따라 늘어나는 재시도 간격. 0 이면 백오프가 걸려
  // 있지 않다는 뜻이고, 성공하면 즉시 0 으로 되돌린다.
  private retryDelayMs = 0;

  // 다음 전송을 허용할 시각(ms epoch). 0 이면 즉시 전송 가능.
  // pending 적재 자체는 절대 막지 않고, flush() 가 실제로 네트워크 요청을
  // 보내는 시점만 이 값으로 지연시킨다.
  private retryAt = 0;

  constructor() {
    makeObservable(this);
  }

  setPublicStore(publicStore: PublicStore) {
    this.publicStore = publicStore;
  }

  /**
   * 한국어 원문을 현재 언어로 번역해 반환한다.
   * 사전에 없으면 원문을 반환하고 배치 큐에 넣는다.
   * 번역이 도착하면 dict 가 바뀌므로 observer 컴포넌트가 다시 그린다.
   *
   * children 처럼 문자열이 아닌 값(JSX 엘리먼트·숫자·배열)이 들어올 수 있는
   * 자리에서도 그대로 부를 수 있게, 순수 텍스트가 아니면 무번역으로 통과시킨다.
   * 번역은 오직 문자열에 대해서만 일어난다 — JSX 를 번역하려다 깨지는 일이 없다.
   * 다만 이 가드는 "문자열이 아님"만 막는다. 문자열이지만 레코드 데이터인
   * 경우(고객명 등)는 여기서 구분할 수 없으므로, 그런 자리는 호출부에서
   * translate={false} 로 꺼야 한다.
   */
  T(ko?: string): string;

  T(node: ReactNode): ReactNode;

  T(value?: ReactNode): ReactNode {
    if (typeof value !== 'string') {
      return value;
    }

    const ko = value;
    if (!ko) {
      return '';
    }

    if (this.lang === 'ko') {
      return ko;
    }

    const hit = this.dict.get(ko);
    if (hit !== undefined) {
      return hit;
    }

    if (!this.isCoolingDown(ko) && !this.inFlight.has(ko)) {
      this.enqueue(ko);
    }

    return ko;
  }

  // 렌더 중에 호출된다. 만료된 항목은 지워서 Map 이 계속 커지지 않게 한다
  // (cooldown 은 observable 이 아니므로 렌더 중 수정해도 MobX 와 무관하다).
  private isCoolingDown(ko: string): boolean {
    const until = this.cooldown.get(ko);
    if (until === undefined) {
      return false;
    }

    if (Date.now() < until) {
      return true;
    }

    this.cooldown.delete(ko);
    return false;
  }

  @action
  async setLang(lang: string): Promise<void> {
    const next = normalizeLanguage(lang);

    if (next === this.lang) {
      // 언어가 실제로 바뀌지 않았다면 이미 쌓인 사전을 지우지 않는다.
      // login() 은 401 재시도마다 t.setLang(this.language) 를 다시 호출하므로,
      // 이전 preload 가 실패했거나 아직 진행 중이었을 뿐인 경우까지 여기서
      // dict/pending/cooldown 을 비우면 배치 번역으로 쌓인 결과가 매번 사라진다.
      if (next !== 'ko' && !this.loaded.has(next)) {
        await this.load();
      }
      return;
    }

    this.lang = next;
    this.dict.clear();
    this.pending.clear();
    this.cooldown.clear();
    // 이전 언어에서 보내 놓고 아직 응답을 못 받은 문구도 새 언어로 물려주지
    // 않는다. 지우지 않으면 T() 가 "이미 in-flight" 로 착각해 새 언어로는
    // 아예 재요청을 넣지 않고, 이전 언어의 응답(최대 25초짜리 axios 타임아웃)
    // 이 올 때까지 그 라벨만 번역 없이 남는다.
    this.inFlight.clear();
    // 반면 flushing 은 여기서 건드리지 않는다. 이 값은 "우리가 뭘 기다리는가"
    // 가 아니라 "지금 이 오리진으로 연결을 하나 쓰고 있는가" 를 뜻하므로,
    // 언어를 바꿨다고 false 로 되돌리면 이전 언어 요청이 아직 살아 있는 채로
    // 두 번째 연결이 열린다. 진행 중인 flush() 의 finally 가 응답이 오는
    // 시점에 스스로 풀고, 남아 있는 pending 도 그 자리에서 다시 예약한다.
    // 이전 언어에서 쌓인 백오프를 새 언어로 물려주지 않는다. 언어를 바꿨다는
    // 것은 사용자가 여전히 화면을 쓰고 있다는 뜻이므로, 관련 없는 실패 이력
    // 때문에 새 언어의 첫 요청까지 늦출 이유가 없다.
    this.retryDelayMs = 0;
    this.retryAt = 0;
    // scheduleFlush() 는 this.timer 하나로 중첩을 막는다. 이전 언어의 백오프가
    // 걸려 있었다면 남은 대기 시간(최대 60초)만큼 이미 재예약된 타이머가
    // 살아 있을 수 있는데, 그걸 그대로 두면 새 언어의 첫 배치가 이 타이머
    // 뒤에 숨어 최대 1분 가까이 지연된다. 방금 초기화한 백오프 숫자와 함께
    // 반드시 같이 지워야 "언어를 바꾸면 백오프도 새로 시작한다"는 의도가
    // 완성된다.
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }

    if (next === 'ko') {
      return;
    }

    await this.load();
  }

  /**
   * 해당 언어 사전 전체를 한 번에 받아온다.
   * 이게 성공하면 이후 T() 는 거의 항상 동기 히트가 된다.
   */
  async load(): Promise<void> {
    const { lang, publicStore } = this;
    if (lang === 'ko' || !publicStore) {
      return;
    }

    try {
      const items = await TranslateRepository.all(publicStore, lang);

      runInAction(() => {
        // 로드 중에 언어가 바뀌었으면 늦게 온 응답은 버린다
        if (lang !== this.lang) {
          return;
        }

        Object.keys(items).forEach((ko) => this.dict.set(ko, items[ko]));
        this.loaded.add(lang);

        // /i18n/all 프리로드가 성공했다는 것도 서버가 살아 있다는 증거다.
        // flush() 의 /i18n/translate 실패로 쌓인 백오프를 여기서도 풀어줘야
        // 프리로드는 이미 성공했는데 배치 번역만 남은 백오프 시간을 다
        // 기다리는 일이 없다.
        this.retryDelayMs = 0;
        this.retryAt = 0;
      });
    } catch (e) {
      // 번역은 부가 기능이다. 화면을 막지 않는다.
      console.warn(`[i18n] dictionary load failed (lang=${lang} ${describeError(e)})`);
    }
  }

  // 렌더 중에 호출된다. observable 을 건드리면 MobX 가 경고한다.
  private enqueue(ko: string) {
    if (this.pending.has(ko)) {
      return;
    }

    this.pending.add(ko);
    this.scheduleFlush();
  }

  // delayMs 를 생략하면 평소의 200ms 디바운스를 쓴다. 백오프 중에는 flush()
  // 가 남은 대기 시간을 직접 넘겨 스스로를 재예약한다. this.timer 하나로
  // 두 용도를 겸하므로, 이미 (디바운스든 백오프든) 타이머가 잡혀 있으면
  // 그대로 반환한다 - scheduleFlush() 가 렌더마다 반복 호출되어도 타이머가
  // 중첩되지 않는다.
  private scheduleFlush(delayMs: number = FLUSH_DELAY_MS) {
    if (this.timer) {
      return;
    }

    this.timer = setTimeout(() => {
      this.timer = undefined;
      this.flush();
    }, delayMs);
  }

  private async flush(): Promise<void> {
    const { lang, publicStore } = this;

    // pending 을 비우기 전에 먼저 가드를 확인한다. publicStore 가 아직 연결되지
    // 않았다면(다음 작업에서 연결 예정) 큐에 그대로 남겨 두어야, 이미 pending 에
    // 있는 문구를 T() 가 다시 enqueue 하지 않고(= 재스케줄하지 않고) 조용히
    // 넘어간다. 먼저 비우고 나중에 가드를 걸면 매 렌더마다 재적재 -> 200ms 마다
    // 아무 일도 하지 않는 flush 를 무한히 예약하는 결과가 된다.
    if (lang === 'ko' || !publicStore || !this.pending.size) {
      return;
    }

    // 이미 요청이 하나 나가 있으면 두 번째 연결을 열지 않는다. 여기서 그냥
    // 돌아가도 대기열이 방치되지는 않는다 - 진행 중인 flush() 는 끝나기
    // 직전에 pending 이 남아 있으면 반드시 다시 예약하기 때문이다.
    if (this.flushing) {
      return;
    }

    // 백오프 대기 중이면 아직 보내지 않는다. T() 는 이미 pending 에 있는
    // 문구를 재적재하지 않으므로(enqueue 의 중복 방지), 여기서 남은 대기
    // 시간만큼 스스로 재예약해 두지 않으면 대기열이 아무도 깨워주지 않는
    // 채로 멈춰버린다.
    const now = Date.now();
    if (now < this.retryAt) {
      this.scheduleFlush(this.retryAt - now);
      return;
    }

    const texts = Array.from(this.pending).slice(0, MAX_BATCH);
    texts.forEach((ko) => {
      this.pending.delete(ko);
      // 응답이 오기 전까지는 pending/dict/cooldown 그 어디에도 없는 상태가 되지
      // 않도록 in-flight 로 표시한다. 그래야 응답을 기다리는 동안(최대 25초)
      // 일어나는 재렌더가 같은 문구를 중복으로 재요청하지 않는다.
      this.inFlight.add(ko);
    });

    this.flushing = true;

    try {
      const items = await TranslateRepository.translate(publicStore, lang, texts);

      runInAction(() => {
        texts.forEach((ko) => this.inFlight.delete(ko));

        // 로드 중에 언어가 바뀌었으면 늦게 온 응답은 버린다
        if (lang !== this.lang) {
          return;
        }

        texts.forEach((ko) => {
          // "값이 있는지"가 아니라 "키가 있는지"로 판단해야 빈 문자열 번역과
          // 누락을 혼동하지 않는다.
          if (Object.prototype.hasOwnProperty.call(items, ko)) {
            this.dict.set(ko, items[ko]);
          } else {
            // 서버는 번역하지 않기로 확정한 문구(한글 없음, 길이 초과, 번역
            // 결과가 원문과 같음)까지 items[text] = text 로 돌려준다. 따라서
            // 키가 빠져 있다는 것은 "번역할 수 없는 문구"가 아니라 "이번엔
            // 못 받았다"는 뜻뿐이다 - 영구 제외가 아니라 쿨다운만 건다.
            this.cooldown.set(ko, Date.now() + ABSENT_RETRY_DELAY_MS);
          }
        });
      });

      // 전송이 성공했으니(응답 내용에서 일부가 거부됐더라도) 백오프를
      // 해제한다 - 서버가 다시 살아 있다는 뜻이므로 다음 요청은 지연 없이
      // 나가야 한다. 단, 응답을 기다리는 동안 언어가 바뀌었다면 이건 "이전
      // 언어" 요청이 살아 있었다는 증거일 뿐이다 - 새 언어가 이미 자신만의
      // 백오프를 쌓아 놓았을 수 있으므로 여기서 무조건 0 으로 되돌리면 그
      // 상태를 지운다(setLang() 은 언어를 바꾸는 시점의 백오프만 지운다).
      if (lang === this.lang) {
        this.retryDelayMs = 0;
        this.retryAt = 0;
      }
    } catch (e) {
      // 전송 오류(타임아웃/401 등)는 "번역을 거부당했다"는 의미가 아니다.
      // 쿨다운에 넣으면 서버가 이미 Redis 에 저장해 둔 번역을 다시 볼 시점이
      // 괜히 뒤로 밀린다. 여기서는 in-flight 표시만 지운다.
      //
      // dict/cooldown 은 이 catch 에서 전혀 건드리지 않으므로(서버가 아예
      // 응답하지 않았다) setLang() 이 cooldown 을 비운 뒤에 오는 이전 언어의
      // 실패 응답이 새 cooldown 을 다시 채우는 일은 구조적으로 불가능하다.
      // in-flight 해제만은 언어와 무관하게 항상 수행해야 문구가 영원히
      // in-flight 로 남지 않는다(요청 4) - setLang() 이 이미 비웠다면 여기
      // delete() 는 아무 것도 지우지 않는 조용한 no-op 이 된다.
      //
      // 오류 객체를 그대로 찍지 않는다. axios 오류의 config.data 에는 방금
      // 보낸 요청 본문(= 인증 토큰 포함)이 통째로 들어 있다.
      console.warn(`[i18n] translate failed (lang=${lang} texts=${texts.length} ${describeError(e)})`);
      texts.forEach((ko) => this.inFlight.delete(ko));

      // 실패한 문구를 직접 pending 으로 되돌린다. 되돌리지 않으면 이 문구들은
      // dict/cooldown/inFlight/pending 그 어디에도 없는 상태가 되고, 오직 해당
      // 컴포넌트가 우연히 다시 그려질 때만 T() 가 되살린다. LabelBox 는 타이머
      // 같은 것으로 주기적으로 다시 그려지지 않으므로(publicStore.now 를 읽는
      // 것은 HeaderMenu 뿐이다) 사용자가 그 화면을 떠났다 돌아오기 전까지 배치
      // 하나가 통째로 사라질 수 있다. 되돌린다고 해서 곧바로 재전송되는 것은
      // 아니다 - 언제 나갈지는 아래 retryAt(지수 백오프)이 정한다.
      //
      // 언어가 그 사이에 바뀌었다면 되돌리지 않는다. setLang() 이 pending 을
      // 비웠고 dict/lang 이 observable 이라 모든 화면이 이미 다시 그려지므로,
      // 새 언어에 필요한 문구는 T() 가 알아서 다시 채운다.
      //
      // retryDelayMs/retryAt 은 언어를 구분하지 않는 단일 필드다.
      // 이 요청을 보낸 뒤 언어가 바뀌었다면(lang !== this.lang) 이 실패는
      // "이전 언어" 의 실패일 뿐인데, 그걸로 새 언어의 백오프를 새로 걸면
      // setLang() 이 방금 초기화해 둔(요청 2) 새 언어의 백오프 상태를
      // 관련 없는 이전 언어의 실패가 덮어써 오염시킨다. 지수 백오프: 실패할
      // 때마다 대기 시간을 2배로 늘리고(2초 -> 60초 상한) 다음 요청은 그
      // 시간이 지난 뒤에만 나가도록 한다. pending 적재(enqueue/T())는 전혀
      // 막지 않는다 - 렌더 중 동기 호출인 T() 가 여기서 지연되면 안 되고,
      // 실제로 늦춰야 하는 건 네트워크 요청뿐이다.
      if (lang === this.lang) {
        texts.forEach((ko) => this.pending.add(ko));

        this.retryDelayMs = this.retryDelayMs
          ? Math.min(this.retryDelayMs * 2, MAX_RETRY_DELAY_MS)
          : INITIAL_RETRY_DELAY_MS;
        this.retryAt = Date.now() + this.retryDelayMs;
      }
    } finally {
      // 성공/실패/언어 변경 어느 경로로 빠져나가든 반드시 풀어야 다음 배치가
      // 나갈 수 있다.
      this.flushing = false;
    }

    // MAX_BATCH 를 넘겨 남은 문구(정상), 방금 실패해 되돌려 놓은 문구(백오프),
    // 요청이 나가 있는 동안 새로 쌓인 문구(단일 요청 가드에 막혀 그냥 돌아간
    // flush 들)를 여기서 이어서 처리한다. 이 블록이 있기 때문에 위의 어떤
    // 조기 반환도 대기열을 아무도 깨워주지 않는 채로 남기지 않는다. 백오프
    // 중이면 즉시(200ms) 재시도하지 않고 남은 대기 시간만큼 늦춰 예약한다.
    if (this.pending.size) {
      const wait = this.retryAt - Date.now();
      this.scheduleFlush(wait > 0 ? wait : undefined);
    }
  }
}

export const t = new TranslateStore();

export default TranslateStore;
